import React, { Suspense, useEffect, useLayoutEffect } from "react";
import { Provider } from "react-redux";
import { useTranslation } from "react-i18next";

import Router from "./router";
import { store, useAppSelector } from "./store";

import AppLayout from "@amzn/awsui-components-react/polaris/app-layout";
import ContentLayout from "@amzn/awsui-components-react/polaris/content-layout";

import Amplify from "components/amplify";
import ErrorBoundary from "components/error-boundary";
import Footer from "components/footer";
import Header from "components/header";
import LeftNavigation from "components/left-navigation";
import Loader from "components/loader";
import PageHeader from "features/page-header";

import Breadcrumbs from "features/breadcrumbs/Breadcrumbs";
import Notifications from "features/notifications/Notifications";
import { AuthN } from "features/auth";
import { useSettings } from "features/settings/hooks";
import { useInfo } from "features/info";
import { useSyncFeatureFlags } from "hooks";

import styles from "./style.module.scss";
import "./i18n";
import { useLocation } from "react-router-dom";
import ActivityLogSplitPanel, {
  useActivityLogSplitPanel,
} from "../features/activity-log-split-panel";
import {
  useFilePreviewSplitPanel,
  FilePreviewSplitPanel,
  AtlasFilePreviewPanel,
} from "../components/file-preview-split-panel";
import {
  ProgressUpdateSplitPanel,
  useProgressUpdateSplitPanel,
} from "../components/progress-update-split-panel";
import { SidebarChatbotDrawer } from "../components/sidebar-chatbot-drawer";
const App: React.FC = (): JSX.Element => {
  useSettings();
  useTranslation();
  useSyncFeatureFlags();

  const isSidebarOpen = useAppSelector((s) => s.assistant.isOpen);
  const [navigationOpen, setNavigationOpen] = React.useState(
    localStorage.getItem("navigationOpen") === "true" || false,
  );
  const requireContentLayout = useAppSelector(
    (s) => s.pageHeader.requireContentLayout,
  );
  const { info, setOpenInfo } = useInfo();

  useLayoutEffect(() => {
    localStorage.setItem("navigationOpen", String(navigationOpen));
  }, [navigationOpen]);

  // Split Panels Management:
  const location = useLocation();
  const [isActivitiesTab, setIsActivitiesTab] = React.useState(false);
  const [_isFilesTab, setIsFilesTab] = React.useState(false);

  // Activity Log Split Panel (restricted to activities tab)
  const {
    splitPanel: activityLogSplitPanel,
    setSelectedActivity: setSelectedActivityForSplitPanel,
    setSplitPanelOpen: setActivityLogSplitPanelOpen,
  } = useActivityLogSplitPanel();

  // File Preview Split Panel
  const {
    splitPanel: filePreviewSplitPanel,
    setSelectedDocumentId: setSelectedDocumentIdForSplitPanel,
    setSplitPanelOpen: setFilePreviewSplitPanelOpen,
    setSource: setFilePreviewSource,
  } = useFilePreviewSplitPanel();

  const { splitPanel: progressUpdateSplitPanel, close: closeProgressUpdate } =
    useProgressUpdateSplitPanel();
  // Close and clear both split panels when navigating between pages
  // This ensures clean transitions and prevents unwanted panel appearances
  const closeAndClearAllSplitPanels = () => {
    // Always clear both panels when navigating to ensure clean transitions
    setSelectedActivityForSplitPanel(null);
    setActivityLogSplitPanelOpen(false);
    setSelectedDocumentIdForSplitPanel(null);
    setFilePreviewSplitPanelOpen(false);
    setFilePreviewSource(null);
  };
  // Progress update survives tab changes on the same page, so its edits aren't lost.
  const previousPathname = React.useRef(location.pathname);
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    // The team activity log page has no `tabId=activities` query param - it is
    // a standalone route, not a tab under an entity page. Recognize that path
    // so the activity split panel mounts the same way it does on task/audit tabs.
    const isCurrentlyActivitiesTab =
      searchParams.get("tabId") === "activities" ||
      /^\/site-admin\/teams\/[^/]+\/activity$/.test(location.pathname);
    const isCurrentlyFilesTab =
      searchParams.get("tabId") === "files" ||
      location.pathname.startsWith("/team-files");

    setIsActivitiesTab(isCurrentlyActivitiesTab);
    setIsFilesTab(isCurrentlyFilesTab);
    closeAndClearAllSplitPanels();
    if (previousPathname.current !== location.pathname) {
      closeProgressUpdate();
    }
    previousPathname.current = location.pathname;
  }, [location]); // Only depend on location changes

  // Determine which split panel is open
  const isSplitPanelOpen =
    activityLogSplitPanel.isOpen ||
    filePreviewSplitPanel.isOpen ||
    progressUpdateSplitPanel.isOpen;

  // Always controlled: Cloudscape warns when splitPanelSize switches modes.
  const splitPanelMode = progressUpdateSplitPanel.isOpen
    ? "progressUpdate"
    : filePreviewSplitPanel.isOpen
      ? "side"
      : "bottom";
  const [splitPanelSizes, setSplitPanelSizes] = React.useState<
    Partial<Record<typeof splitPanelMode, number>>
  >({});
  const defaultSplitPanelSize = {
    progressUpdate: document.documentElement.clientWidth / 3,
    side: document.documentElement.clientWidth / 3,
    bottom: document.documentElement.clientHeight / 2,
  }[splitPanelMode];

  useEffect(() => {
    localStorage.setItem("splitPanelOpen", String(isSplitPanelOpen));
  }, [isSplitPanelOpen]);

  const handleSplitPanelToggle = ({
    detail,
  }: {
    detail: { open: boolean };
  }) => {
    if (!detail.open) {
      // Close both panels when user manually closes
      setActivityLogSplitPanelOpen(false);
      setFilePreviewSplitPanelOpen(false);
      closeAndClearAllSplitPanels();
      closeProgressUpdate();
    }
  };

  return (
    <Amplify>
      <AuthN>
        <div className={styles.awsui}>
          <ErrorBoundary>
            <Header />

            <AppLayout
              disableContentPaddings
              minContentWidth={1}
              maxContentWidth={Number.MAX_VALUE}
              headerSelector="#header"
              stickyNotifications={true}
              notifications={<Notifications />}
              navigation={<LeftNavigation />}
              navigationOpen={navigationOpen}
              onNavigationChange={({ detail }) =>
                setNavigationOpen(detail.open)
              }
              splitPanelOpen={isSplitPanelOpen}
              onSplitPanelToggle={handleSplitPanelToggle}
              splitPanelPreferences={{
                position: splitPanelMode === "bottom" ? "bottom" : "side",
              }}
              splitPanelSize={
                splitPanelSizes[splitPanelMode] ?? defaultSplitPanelSize
              }
              onSplitPanelResize={({ detail }) =>
                setSplitPanelSizes((sizes) => ({
                  ...sizes,
                  [splitPanelMode]: detail.size,
                }))
              }
              splitPanel={
                progressUpdateSplitPanel.isOpen ? (
                  <ProgressUpdateSplitPanel />
                ) : // Show file preview panel when file is selected
                filePreviewSplitPanel.isOpen ? (
                  filePreviewSplitPanel.source === "atlas" ? (
                    <AtlasFilePreviewPanel />
                  ) : (
                    <FilePreviewSplitPanel />
                  )
                ) : // Show activity log panel only when activities tab is active AND activity is selected
                isActivitiesTab && activityLogSplitPanel.isOpen ? (
                  <ActivityLogSplitPanel
                    item={activityLogSplitPanel.selectedActivity}
                    isActivitiesTab={isActivitiesTab}
                  />
                ) : (
                  // Fallback: empty div when no panel should be shown
                  <div />
                )
              }
              breadcrumbs={<Breadcrumbs />}
              content={
                <div
                  style={{
                    paddingInlineStart: 40,
                    paddingInlineEnd: isSidebarOpen ? 10 : 40,
                  }}
                >
                  <ErrorBoundary>
                    {requireContentLayout && (
                      <ContentLayout header={<PageHeader />}>
                        <Router />
                      </ContentLayout>
                    )}

                    {!requireContentLayout && <Router />}
                  </ErrorBoundary>
                </div>
              }
              tools={info.tools}
              toolsOpen={info.toolsOpen}
              onToolsChange={({ detail }) => setOpenInfo(detail.open)}
              footerSelector="#footer"
            />

            <Footer />
          </ErrorBoundary>
          <SidebarChatbotDrawer />
        </div>
      </AuthN>
    </Amplify>
  );
};

const AppProvider: React.FC = (): JSX.Element => {
  return (
    <Provider store={store}>
      <Suspense fallback={<Loader loadingText="Loading..." />}>
        <App />
      </Suspense>
    </Provider>
  );
};

export default AppProvider;
